import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { apiUrl } from '../runtime'
import { apiFetch } from './apiClient'
import {
  clearSession,
  getAccessToken,
  getStoredUser,
  setAccessToken,
  storeSession,
  type StoredUser,
} from './tokenStore'

/** An invite or login token read from the URL and not yet exchanged. */
export interface PendingLink {
  kind: 'invite' | 'login'
  token: string
}

export interface AuthState {
  user: StoredUser | null
  loading: boolean
  pendingLink: PendingLink | null
  /** The last pending link was spent or expired; the login page offers a new one. */
  linkExpired: boolean
}

export interface AuthContextValue extends AuthState {
  requestMagicLink: (
    email: string,
    redirectUrl?: string,
  ) => Promise<{ status: string; sent: boolean; login_url?: string | null }>
  /**
   * Exchange the pending link for a session. 'invalid' = the server rejected
   * the token (spent or expired), the link is dropped. 'unavailable' = the
   * server or network failed, the link is kept so the person can retry.
   */
  claimPendingLink: () => Promise<'ok' | 'invalid' | 'unavailable'>
  /** Keep the current session and forget the link in the URL. */
  dismissPendingLink: () => void
  logout: () => Promise<void>
  updatePhone: (phone: string) => Promise<StoredUser>
}

interface MeResponse {
  id: number | null
  email: string
  name: string | null
  role: string
  is_admin: boolean
  whatsapp_enabled: boolean
  phone: string | null
  agent_name: string
}

interface TokenResponse {
  access_token: string
  token_type: 'bearer'
  expires_in: number
}

const AuthContext = createContext<AuthContextValue | null>(null)

const TOKEN_PARAMS = ['invite_token', 'login_token', 'token'] as const

async function fetchMeAndStore(token: string): Promise<StoredUser | null> {
  const response = await fetch(apiUrl('/api/auth/me'), {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) return null
  const me = (await response.json()) as MeResponse
  storeSession(token, me)
  return getStoredUser()
}

async function fetchMeWithoutToken(): Promise<StoredUser | null> {
  const response = await fetch(apiUrl('/api/auth/me'), {
    credentials: 'include',
  })
  if (!response.ok) return null
  return (await response.json()) as StoredUser
}

function devAuthBypassEnabled(): boolean {
  return import.meta.env.VITE_PROGETTO_ARCA_AUTH_DISABLED === 'true'
}

/**
 * POST the token. Returns the user on success, null when the server rejected
 * the token (4xx). Throws on a 5xx or a network failure, and — after the
 * token was accepted — when /me fails: the session then exists server-side,
 * so the caller must not report the link as spent.
 */
async function exchangeUrlToken(
  path: string,
  body: Record<string, string>,
): Promise<StoredUser | null> {
  const tokenResponse = await fetch(apiUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (tokenResponse.status >= 500) throw new Error(`exchange failed: ${tokenResponse.status}`)
  if (!tokenResponse.ok) return null
  const data = (await tokenResponse.json()) as TokenResponse
  setAccessToken(data.access_token)
  const user = await fetchMeAndStore(data.access_token)
  if (!user) throw new Error('token accepted but /me failed')
  return user
}

function exchangePendingLink(link: PendingLink): Promise<StoredUser | null> {
  return link.kind === 'invite'
    ? exchangeUrlToken('/api/auth/claim-invite', { invite_token: link.token })
    : exchangeUrlToken('/api/auth/verify-magic-link', { token: link.token })
}

async function silentRefresh(): Promise<StoredUser | null> {
  const tokenResponse = await fetch(apiUrl('/api/auth/refresh'), {
    method: 'POST',
    credentials: 'include',
  })
  if (!tokenResponse.ok) return null
  const data = (await tokenResponse.json()) as TokenResponse
  setAccessToken(data.access_token)
  return fetchMeAndStore(data.access_token)
}

/** Remove every token parameter from the address bar. */
export function stripTokenParams(): void {
  let nextUrl: URL
  try {
    nextUrl = new URL(window.location.href)
  } catch {
    return
  }
  for (const name of TOKEN_PARAMS) nextUrl.searchParams.delete(name)
  try {
    const relative = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`
    window.history.replaceState({}, '', relative)
  } catch {
    // best-effort in environments without a real History API
  }
  // If window.location is a plain URL value (e.g. test override), mutate it in
  // place so subsequent reads of window.location.search reflect the cleaned URL.
  if (window.location instanceof URL) {
    for (const name of TOKEN_PARAMS) window.location.searchParams.delete(name)
  }
}

/** Drop the token parameters from a search string (for redirect targets). */
export function withoutTokenParams(search: string): string {
  const params = new URLSearchParams(search)
  for (const name of TOKEN_PARAMS) params.delete(name)
  const clean = params.toString()
  return clean ? `?${clean}` : ''
}

/**
 * Read an invite or login token out of the URL without exchanging it. Mail
 * scanners render the page seconds after delivery; spending the single-use
 * token on load would hand them the session and lock the person out. The
 * token stays in the address bar until claimPendingLink, so a reload or an
 * "open in browser" hand-off keeps it. A login token wins over an invite
 * token: it is always the fresher of the two.
 */
function readPendingLink(): PendingLink | null {
  const params = new URLSearchParams(window.location.search)
  const loginToken = params.get('login_token') || params.get('token')
  const inviteToken = params.get('invite_token')
  if (loginToken) return { kind: 'login', token: loginToken }
  if (inviteToken) return { kind: 'invite', token: inviteToken }
  return null
}

async function bootstrapSession(): Promise<StoredUser | null> {
  const stored = getStoredUser()
  const storedToken = getAccessToken()
  if (storedToken && stored) {
    const refreshed = await fetchMeAndStore(storedToken)
    if (refreshed) return refreshed
  }

  if (devAuthBypassEnabled()) {
    const devBypassUser = await fetchMeWithoutToken()
    if (devBypassUser) return devBypassUser
  }

  const viaRefresh = await silentRefresh()
  if (viaRefresh) return viaRefresh

  if (!devAuthBypassEnabled()) {
    const devBypassUser = await fetchMeWithoutToken()
    if (devBypassUser) return devBypassUser
  }

  clearSession()
  return null
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const cached = getStoredUser()
    return { user: cached, loading: true, pendingLink: null, linkExpired: false }
  })

  useEffect(() => {
    let cancelled = false
    const pendingLink = readPendingLink()
    bootstrapSession()
      .catch(() => null)
      .then((user) => {
        if (!cancelled) setState({ user, loading: false, pendingLink, linkExpired: false })
      })
    return () => {
      cancelled = true
    }
  }, [])

  const claimPendingLink = useCallback(async () => {
    const link = state.pendingLink
    if (!link) return 'invalid' as const
    let user: StoredUser | null
    try {
      user = await exchangePendingLink(link)
    } catch {
      // The token may already be spent server-side (accepted, then /me
      // failed); the access token just received or the refresh cookie
      // recovers that session.
      const accessToken = getAccessToken()
      const recovered =
        (accessToken ? await fetchMeAndStore(accessToken).catch(() => null) : null) ??
        (await bootstrapSession().catch(() => null))
      if (recovered) {
        stripTokenParams()
        setState({ user: recovered, loading: false, pendingLink: null, linkExpired: false })
        return 'ok' as const
      }
      return 'unavailable' as const
    }
    stripTokenParams()
    setState({ user, loading: false, pendingLink: null, linkExpired: !user })
    return user ? ('ok' as const) : ('invalid' as const)
  }, [state.pendingLink])

  const dismissPendingLink = useCallback(() => {
    stripTokenParams()
    setState((current) => ({ ...current, pendingLink: null }))
  }, [])

  const requestMagicLink = useCallback(
    async (email: string, redirectUrl?: string) => {
      const response = await fetch(apiUrl('/api/auth/magic-link'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          redirect_url: redirectUrl ?? `${window.location.origin}/login`,
        }),
      })
      return (await response.json()) as {
        status: string
        sent: boolean
        login_url?: string | null
      }
    },
    [],
  )

  const logout = useCallback(async () => {
    try {
      await apiFetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
    } catch {
      // best-effort
    }
    clearSession()
    setState({ user: null, loading: false, pendingLink: null, linkExpired: false })
  }, [])

  const updatePhone = useCallback(async (phone: string) => {
    const response = await apiFetch('/api/auth/me', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone }),
    })
    if (!response.ok) {
      const payload = await response.json().catch(() => ({})) as { detail?: string }
      throw new Error(payload.detail || 'The phone number was not saved')
    }
    const user = (await response.json()) as StoredUser
    const token = getAccessToken()
    if (token) storeSession(token, user)
    setState((current) => ({ ...current, user, loading: false }))
    return user
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, requestMagicLink, claimPendingLink, dismissPendingLink, logout, updatePhone }),
    [state, requestMagicLink, claimPendingLink, dismissPendingLink, logout, updatePhone],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>')
  }
  return ctx
}
